import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { avatarTint, iniciales, saludo, fechaLarga, primerNombre } from '../lib/identity';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { BrandLockup, BrandTile } from './BrandMark';
import { CommandPalette, accionesPorRol } from './CommandPalette';
import {
  LayoutDashboard,
  Calendar,
  Users,
  Stethoscope,
  Settings,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  LogOut,
  Menu,
  X,
  UserCog,
  ClipboardList,
  BarChart2,
  UtensilsCrossed,
  UserPlus,
  Flame,
  Search,
  Bell,
  RefreshCw,
  Command as CommandIcon,
} from 'lucide-react';

import { API } from '@/lib/api';

/**
 * Navegación agrupada por momento de uso (no por tipo de entidad):
 * primero "Hoy" (lo que se hace al llegar), luego la clínica, luego gestión.
 * Los contadores viven en los grupos, así el menú también informa.
 */
const NAV_GROUPS = [
  {
    label: 'Hoy',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard',   path: '/admin/dashboard',     roles: ['admin'],               badge: 'citas' },
      { icon: Stethoscope,     label: 'Mi Panel',    path: '/doctor/dashboard',    roles: ['doctor'] },
      { icon: ClipboardList,   label: 'Recepción',   path: '/recepcion/dashboard', roles: ['recepcion'],           badge: 'citas' },
      { icon: Calendar,        label: 'Calendario',  path: '/calendar',            roles: ['admin', 'doctor', 'recepcion'] },
    ],
  },
  {
    label: 'Clínica',
    items: [
      { icon: Users,           label: 'Pacientes',   path: '/patients',            roles: ['admin', 'doctor', 'recepcion'] },
      { icon: UserPlus,        label: 'Prospectos',  path: '/prospectos',          roles: ['admin', 'doctor', 'recepcion'], badge: 'prospectos' },
      { icon: Flame,           label: 'Reto 21 días',path: '/reto21',              roles: ['admin', 'doctor'] },
      { icon: UtensilsCrossed, label: 'Dietas',      path: '/alimentos',           roles: ['admin', 'doctor'] },
    ],
  },
  {
    label: 'Gestión',
    items: [
      { icon: UserCog,   label: 'Equipo',        path: '/doctors',    roles: ['admin'] },
      { icon: BarChart2, label: 'Reportes',      path: '/reports',    roles: ['admin'] },
      { icon: Settings,  label: 'Configuración', path: '/settings',   roles: ['admin', 'doctor', 'recepcion'] },
    ],
  },
];

const useContadores = (rol) => {
  const [datos, setDatos] = useState({ citasHoy: null, porConfirmar: 0, prospectos: null, enLinea: true });

  const cargar = useCallback(async () => {
    const hoy = new Date().toISOString().split('T')[0];
    try {
      const [citas, prospectos] = await Promise.all([
        axios.get(`${API}/appointments`, { params: { fecha: hoy, limit: 300 } }),
        rol === 'doctor' ? Promise.resolve({ data: { total: null } })
          : axios.get(`${API}/patients/count`, { params: { solo_prospectos: true, seguimiento: 'sin' } }),
      ]);
      const lista = Array.isArray(citas.data) ? citas.data : [];
      setDatos({
        citasHoy: lista.length,
        porConfirmar: lista.filter((c) => c.estado === 'citado').length,
        prospectos: prospectos.data?.total ?? null,
        enLinea: true,
      });
    } catch {
      setDatos((d) => ({ ...d, enLinea: false }));
    }
  }, [rol]);

  useEffect(() => {
    cargar();
    const id = setInterval(cargar, 60000);        // refresco suave, sin recargar
    const onFocus = () => cargar();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [cargar]);

  return { ...datos, recargar: cargar };
};

export const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const rol = user?.rol || 'doctor';
  const { citasHoy, porConfirmar, prospectos, enLinea, recargar } = useContadores(rol);

  const grupos = useMemo(
    () => NAV_GROUPS
      .map((g) => ({ ...g, items: g.items.filter((i) => i.roles.includes(rol)) }))
      .filter((g) => g.items.length > 0),
    [rol],
  );

  const navItems = useMemo(() => grupos.flatMap((g) => g.items), [grupos]);
  const acciones = useMemo(() => accionesPorRol(rol), [rol]);

  const handleLogout = () => { logout(); navigate('/login'); };

  // Acciones disparadas desde la paleta de comandos (⌘K)
  useEffect(() => {
    const onAccion = (e) => {
      const { accion } = e.detail || {};
      if (accion === 'nueva-cita') navigate('/calendar', { state: { accion: 'nueva-cita' } });
      else if (accion === 'nuevo-paciente') navigate('/patients', { state: { accion: 'nuevo-paciente' } });
      else if (accion === 'ir-calendario') navigate('/calendar');
      else if (accion === 'ir-pacientes') navigate('/patients');
    };
    window.addEventListener('dentu:accion', onAccion);
    return () => window.removeEventListener('dentu:accion', onAccion);
  }, [navigate]);

  const contador = (clave) => {
    if (clave === 'citas' && citasHoy != null) return citasHoy;
    if (clave === 'prospectos' && prospectos != null) return prospectos;
    return null;
  };

  const navBadge = (item, compacto = false) => {
    const n = item.badge ? contador(item.badge) : null;
    if (n == null || n === 0) return null;
    if (compacto) {
      return <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-brand-from ring-2 ring-card" />;
    }
    return (
      <span className={cn(
        'ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold tnum',
        item.badge === 'citas'
          ? 'bg-primary/10 text-primary'
          : 'bg-warning/15 text-warning',
      )}>
        {n}
      </span>
    );
  };

  return (
    <TooltipProvider delayDuration={250}>
      <div className="min-h-screen bg-background">
        {/* ── Header móvil ───────────────────────────────────────────────── */}
        <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 border-b border-border/70 bg-card/95 backdrop-blur-md flex items-center justify-between px-3">
          <Button
            variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <BrandLockup compact />
          <Button variant="ghost" size="icon" onClick={() => setPaletteOpen(true)} aria-label="Buscar">
            <Search className="h-5 w-5" />
          </Button>
        </header>

        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-foreground/40 backdrop-blur-[2px] animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* ── Barra lateral ──────────────────────────────────────────────── */}
        <aside
          className={cn(
            'fixed top-0 left-0 z-50 h-full flex flex-col border-r border-border/70 bg-card transition-[width,transform] duration-300 ease-smooth',
            collapsed ? 'w-[76px]' : 'w-[248px]',
            mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          )}
          data-testid="sidebar"
          aria-label="Barra lateral"
        >
          <div className={cn('flex h-16 items-center border-b border-border/70', collapsed ? 'justify-center px-2' : 'px-4')}>
            {collapsed ? <BrandTile className="h-9 w-9" iconClass="h-4.5 w-4.5" /> : <BrandLockup />}
          </div>

          <nav className="flex-1 overflow-y-auto scrollbar-thin px-2.5 py-3" aria-label="Navegación principal">
            {grupos.map((grupo, gi) => (
              <div key={grupo.label} className={cn(gi > 0 && 'mt-5')}>
                {!collapsed && (
                  <p className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">
                    {grupo.label}
                  </p>
                )}
                {collapsed && gi > 0 && <div className="mx-auto mb-3 h-px w-6 bg-border" />}
                <div className="space-y-0.5">
                  {grupo.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    const contenido = (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                        aria-current={isActive ? 'page' : undefined}
                        aria-label={item.label}
                        className={cn(
                          'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] font-medium transition-colors duration-150',
                          collapsed && 'justify-center px-0',
                          isActive
                            ? 'bg-primary/[0.09] text-primary'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        {isActive && (
                          <span className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />
                        )}
                        <item.icon className={cn('h-[18px] w-[18px] shrink-0', isActive && 'text-primary')} />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                        {!collapsed && navBadge(item)}
                        {collapsed && navBadge(item, true)}
                      </Link>
                    );

                    return collapsed ? (
                      <Tooltip key={item.path}>
                        <TooltipTrigger asChild>{contenido}</TooltipTrigger>
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      </Tooltip>
                    ) : contenido;
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="space-y-1 border-t border-border/70 p-2.5">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                collapsed && 'justify-center px-0',
              )}
            >
              <CommandIcon className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && (
                <>
                  <span>Buscar o ir a…</span>
                  <kbd className="ml-auto rounded border border-border bg-surface px-1.5 py-0.5 text-[10px] font-semibold">⌘K</kbd>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={toggleTheme}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                collapsed && 'justify-center px-0',
              )}
              data-testid="theme-toggle"
              aria-label={theme === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
            >
              {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
              {!collapsed && <span>{theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}</span>}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg p-1.5 text-left transition-colors hover:bg-muted',
                    collapsed && 'justify-center p-0',
                  )}
                  data-testid="user-menu-btn"
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="text-[11px] font-semibold text-white" style={avatarTint(user?.nombre || 'U')}>
                      {iniciales(user?.nombre || '', 'U')}
                    </AvatarFallback>
                  </Avatar>
                  {!collapsed && (
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold leading-tight">{user?.nombre}</p>
                      <p className="truncate text-[11px] capitalize text-muted-foreground">{user?.rol}</p>
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <p className="text-sm font-semibold">{user?.nombre}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" /> Configuración
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className="hidden w-full items-center justify-center rounded-lg py-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:flex"
              data-testid="collapse-sidebar-btn"
              aria-label={collapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>
        </aside>

        {/* ── Contenido ──────────────────────────────────────────────────── */}
        <div className={cn('transition-[margin] duration-300 ease-smooth', collapsed ? 'lg:ml-[76px]' : 'lg:ml-[248px]')}>
          {/* Barra superior: quién eres, qué hora es y qué sigue */}
          <div className="sticky top-0 z-30 hidden border-b border-border/70 bg-background/85 backdrop-blur-md lg:block">
            <div className="flex h-16 items-center gap-4 px-6 xl:px-8">
              <div className="min-w-0">
                <h2 className="truncate font-display text-[15px] font-bold tracking-tight">
                  {saludo()}, {primerNombre(user?.nombre)}
                </h2>
                <p className="truncate text-xs text-muted-foreground">
                  <span className="first-letter:uppercase">{fechaLarga()}</span>
                </p>
              </div>

              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                className="group ml-auto flex h-9 w-full max-w-[340px] items-center gap-2 rounded-lg border border-border/70 bg-card px-3 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
              >
                <Search className="h-4 w-4" />
                <span className="truncate">Buscar pacientes, citas…</span>
                <kbd className="ml-auto rounded border border-border bg-surface px-1.5 py-0.5 text-[10px] font-semibold">⌘K</kbd>
              </button>

              {/* Estado en vivo: la clínica está pasando ahora mismo */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={recargar}
                    className="flex items-center gap-2 rounded-lg border border-border/70 bg-card px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <span className={cn('relative flex h-2 w-2')}>
                      <span className={cn('absolute inline-flex h-full w-full rounded-full opacity-75', enLinea ? 'animate-pulse-ring bg-success' : 'bg-destructive')} />
                      <span className={cn('relative inline-flex h-2 w-2 rounded-full', enLinea ? 'bg-success' : 'bg-destructive')} />
                    </span>
                    <span className="tnum">{citasHoy ?? '—'} hoy</span>
                    <RefreshCw className="h-3.5 w-3.5 opacity-60" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Se actualiza solo cada minuto · clic para refrescar</TooltipContent>
              </Tooltip>

              {porConfirmar > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => navigate('/calendar')}
                      className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 bg-card text-muted-foreground transition-colors hover:text-foreground"
                      aria-label={`${porConfirmar} citas por confirmar`}
                    >
                      <Bell className="h-4 w-4" />
                      <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-warning px-1 text-[10px] font-bold text-white tnum">
                        {porConfirmar}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{porConfirmar} {porConfirmar === 1 ? 'cita' : 'citas'} sin confirmar</TooltipContent>
                </Tooltip>
              )}

              <Button
                size="sm"
                className="h-9 shrink-0 gap-1.5 bg-brand-gradient text-white shadow-[var(--shadow-glow)] hover:opacity-95"
                onClick={() => navigate('/calendar', { state: { accion: 'nueva-cita' } })}
              >
                <Calendar className="h-4 w-4" /> Nueva cita
              </Button>
            </div>
          </div>

          <main className="min-h-screen pt-14 lg:pt-0">
            <div className="mx-auto w-full max-w-[1400px] p-4 pb-16 md:p-6 lg:p-7 lg:pb-20">
              {/* key por ruta: cada pantalla entra con su propia animación */}
              <div key={location.pathname} className="animate-fade-up">
                {children}
              </div>
            </div>
          </main>
        </div>

        <CommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          navItems={navItems}
          acciones={acciones}
        />
      </div>
    </TooltipProvider>
  );
};
